import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, Phone, Briefcase, Building2, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS_PROFESSIONAL = [
  { key: 'phone', label: 'Phone Number', placeholder: '+61 4xx xxx xxx', type: 'tel', icon: Phone },
  { key: 'employer', label: 'Employer / Company', placeholder: 'e.g. Google, Deloitte', type: 'text', icon: Briefcase },
  { key: 'job_title', label: 'Job Title', placeholder: 'e.g. Software Engineer', type: 'text', icon: User },
  { key: 'linkedin', label: 'LinkedIn Profile (optional)', placeholder: 'linkedin.com/in/yourname', type: 'url', icon: User },
];

const FIELDS_LANDLORD = [
  { key: 'phone', label: 'Phone Number', placeholder: '+61 4xx xxx xxx', type: 'tel', icon: Phone },
  { key: 'company', label: 'Company / Agency Name (if applicable)', placeholder: 'e.g. Smith Properties', type: 'text', icon: Building2 },
  { key: 'abn', label: 'ABN (optional)', placeholder: 'e.g. 51 824 753 556', type: 'text', icon: Briefcase },
  { key: 'bio', label: 'Short Bio', placeholder: 'Tell students about yourself and your properties...', type: 'textarea', icon: User },
];

export default function ProfileSetup() {
  const { user, token, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isLandlord = user?.role === 'landlord';
  const fields = isLandlord ? FIELDS_LANDLORD : FIELDS_PROFESSIONAL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // In a real app, PATCH /api/auth/profile with form data
    // For now, update localStorage user data
    await new Promise(r => setTimeout(r, 800)); // simulate save
    const updatedUser = { ...user, ...form, profile_complete: true };
    login(token, updatedUser);
    setDone(true);
    setTimeout(() => {
      navigate(isLandlord ? '/landlord-dashboard' : '/student-dashboard');
    }, 1500);
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">Profile Complete!</h2>
          <p className="text-gray-500 mt-2">Taking you to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-0 mb-3">
            <span className="text-3xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '2.2rem', width: 'auto', marginLeft: '0.06em', marginBottom: '0.03em', verticalAlign: 'middle' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Complete Your Profile</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Welcome{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}! A few more details to get started as a{' '}
            <span className="text-[#094f6f] font-semibold">{isLandlord ? 'Landlord' : 'Professional'}</span>.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-sky-100 p-7">
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map(({ key, label, placeholder, type, icon: Icon }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
                {type === 'textarea' ? (
                  <textarea
                    value={form[key] || ''}
                    onChange={e => setForm(p => ({...p, [key]: e.target.value}))}
                    placeholder={placeholder}
                    rows={3}
                    className="w-full px-3 py-2.5 border border-sky-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 focus:border-[#094f6f]"
                  />
                ) : (
                  <div className="relative">
                    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={type}
                      value={form[key] || ''}
                      onChange={e => setForm(p => ({...p, [key]: e.target.value}))}
                      placeholder={placeholder}
                      className="pl-10 h-11 border-sky-200 focus:border-[#094f6f]"
                    />
                  </div>
                )}
              </div>
            ))}

            <button
              type="submit"
              disabled={submitting}
              className="w-full brand-gradient-bg text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mt-2"
              data-testid="profile-setup-submit"
            >
              {submitting ? 'Saving...' : 'Save & Go to Dashboard'}
            </button>
            <button
              type="button"
              onClick={() => navigate(isLandlord ? '/landlord-dashboard' : '/student-dashboard')}
              className="w-full text-gray-400 text-sm hover:text-gray-600 transition-colors"
            >
              Skip for now
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
